import {
  EVENT_NAME_INTERACTION,
  TEALIUM_PAGE_CLASSIFY,
  TEALIUM_PAGE_SELECT_AD,
  TEALIUM_PAGE_PAY_AND_POST,
  TEALIUM_PAGE_ROLE_REQS,
  TEALIUM_PAGE_WRITE,
} from 'src/app/analytics';
import {
  ADOBE_VISITOR_ID,
  SITE_SECTION,
  SUB_SECTION,
} from 'src/app/constants/tealium';
import type { Tealium } from 'src/types/appState';

import { sendUtagLinkEvent } from './utils/sendUtagLinkEvent';

const getAnalyticsLinkName = (page: string): string => {
  switch (page) {
    case TEALIUM_PAGE_CLASSIFY:
      return 'ecaja-classify-continue-clicked';
    case TEALIUM_PAGE_SELECT_AD:
      return 'ecaja-select-ad-continue-clicked';
    case TEALIUM_PAGE_PAY_AND_POST:
      return 'caja-post-ad-clicked';
    case TEALIUM_PAGE_ROLE_REQS:
      return 'ecaja-shortlist-continue-clicked';
    case TEALIUM_PAGE_WRITE:
      return 'ecaja-write-continue-clicked';
    default:
      return 'ecaja-continue-clicked';
  }
};

const getEventDataByPage = (
  tealium: Tealium,
  isHorizonHirer?: boolean,
): string[] => {
  const { page } = tealium;

  switch (page) {
    case TEALIUM_PAGE_CLASSIFY:
      return isHorizonHirer ? ['variable-ad-offered'] : [];
    default:
      return [];
  }
};

const getObjectInteractionByPage = (page: string): string => {
  switch (page) {
    case TEALIUM_PAGE_PAY_AND_POST:
      return 'checkout-continue';
    case TEALIUM_PAGE_ROLE_REQS:
      return 'shortlist-continue';
    case TEALIUM_PAGE_WRITE:
      return 'write-continue';
    default:
      return 'classify-continue';
  }
};

interface ClassifyUtagData {
  advertiserTypeHirer: string;
  advertiserTypePricing: string;
  autoClassifyCategoryChosen: string;
  autoClassifyCategorySuggested: string;
  cajaPayType: string;
  cajaSalaryCurrency: string;
  cajaWorkType: string;
  draftId: string;
  hirerContractAdType: string;
  hirerContractBalance: number;
  hirerContractType: string;
  hirerContractValue: number;
  jobAdClassification: string;
  jobAdSubClassification: string;
  recommendedSalaryRange: string;
  roleTitle: string;
  salaryRangeSelected: boolean;
}
interface WriteUtagData {
  advertiserTypeHirer: string;
  advertiserTypePricing: string;
  cajaAboutBusinessCharacterCount: string;
  finalJobSummaryCharacterCount: string;
  numberOfBenefits: number;
  numberOfQualifications: number;
  numberOfTasks: number;
}

interface ManageUtagData {
  isLinkOutApplyViaSeek: boolean | null;
}

type UtagPageData =
  | ClassifyUtagData
  | ManageUtagData
  | WriteUtagData
  | Record<string, unknown>;

export const getUtagDataByPage = (tealium: Tealium): UtagPageData => {
  const { page } = tealium;

  switch (page) {
    case TEALIUM_PAGE_PAY_AND_POST:
      return {};
    case TEALIUM_PAGE_ROLE_REQS:
      return {
        isLinkOutApplyViaSeek: tealium.isLinkOutApplyViaSeek,
      };
    case TEALIUM_PAGE_WRITE:
      return {
        advertiserTypeHirer: tealium.advertiser.type,
        advertiserTypePricing: tealium.advertiser.pricingType,
        cajaAboutBusinessCharacterCount:
          tealium.cajaAboutBusinessCharacterCount,
        finalJobSummaryCharacterCount: tealium.finalJobSummaryCharacterCount,
        numberOfBenefits: tealium.numberOfBenefits,
        numberOfQualifications: tealium.numberOfQualifications,
        numberOfTasks: tealium.numberOfTasks,
      };
    default:
      return {
        advertiserTypeHirer: tealium.advertiser.type,
        advertiserTypePricing: tealium.advertiser.pricingType,
        autoClassifyCategoryChosen: tealium.autoClassifyCategoryChosen,
        autoClassifyCategorySuggested: tealium.autoClassifyCategorySuggested,
        cajaPayType: tealium.cajaPayType,
        cajaSalaryCurrency: tealium.cajaSalaryCurrency,
        cajaWorkType: tealium.cajaWorkType,
        draftId: tealium.draftId,
        jobAdClassification: tealium.jobAdClassification,
        jobAdSubClassification: tealium.jobAdSubClassification,
        priceHidden: tealium.priceHidden,
        recommendedSalaryRange: tealium.recommendedSalaryRange,
        roleTitle: tealium.roleTitle,
        salaryRangeSelected: tealium.salaryRangeSelected,
      };
  }
};

export default (tealium: Tealium, isHorizonHirer?: boolean) => {
  const { draftId, page, seekAdvertiserId, testingId, userId } = tealium;

  const ANALYTICS_LINK_NAME = getAnalyticsLinkName(page);
  const eventName = [
    EVENT_NAME_INTERACTION,
    ANALYTICS_LINK_NAME,
    ...getEventDataByPage(tealium, isHorizonHirer),
  ];

  const basicUtagData = {
    adobeVisitorId: ADOBE_VISITOR_ID,
    draftId,
    page,
    seekAdvertiserId,
    siteSection: SITE_SECTION,
    subSection: SUB_SECTION,
    testingId,
    userId,
  };

  const objectInteraction = getObjectInteractionByPage(page);

  const pageUtagData = getUtagDataByPage(tealium);

  const utagData = {
    ...basicUtagData,
    ...pageUtagData,
    objectInteraction,
    linkName: ANALYTICS_LINK_NAME,
  };

  sendUtagLinkEvent(eventName, utagData);
};
